import React, { useState, useEffect, useMemo } from 'react';
import GoogleMapReact from 'google-map-react';
import { Pointer, SimplePointer } from 'src/App/HOC';
import {
  applySmartBounds,
  calculateBounds,
  coordDistance,
  formatGoogleMapsBounds,
  getBoundedOTAListings,
  getDiscoveryMapViewParamsCountryWise,
  getDynamicMarkers,
  getVisibleListings,
  getVisibleOTAListings,
  isPointWithinBounds,
  mapOptions,
} from 'src/utils/functions';
import { Bounds, Coordinates, Listing } from 'src/interfaces/idashboard';
import { COLOR_PRIMARY_DARK, POINTER_GREEN } from 'src/const';
import {
  comparePolygonLimitsToBoundary,
  extractLatLong,
  getPolygonPaths,
  sortListingBasedOnKey,
} from '../helper';
import { MapListingCountInfoBox } from 'src/App/HOC/Alerts/MapListingCountInfoBoxAlert';
import MapFilter from './MapFilter';
import MarketPointer from 'src/App/HOC/Pointer/MarketPointer';
import DiscoverCountryPointer from 'src/App/HOC/Pointer/DiscoverCountryPointer';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';
import { useDispatch, useSelector } from 'react-redux';
import SearchBarModal from 'src/App/Components/Modals/SearchBarModal';
import * as turf from '@turf/turf';
import { logAmplitudeEvent } from 'src/utils/analytics';

const MAP_FILL_COLOR = POINTER_GREEN;

const Map = ({
  matches,
  search,
  dashboardLayoutView,
  setDashboardLayoutView,
  listings,
  otaListings,
  multipleBookingMarkersCallLoading,
  isDisabled,
  isUserAllowed,
  revPotentialRangeInvestment,
  currency,
  filteredListings,
  filteredOTAListings,
  handleOnSampleClick,
  applyFilter,
  filterActive,
  setShowPremiumDataDynamic,
  toggleDrawer,
  setFilterVal,
  filterVal,
  setFilterActive,
  noDataInMarket,
  topPerformingListingOnFocus,
  setListingsGridStartIndexOffset,
  dummyMarketData,
  discoveryMarkets,
  setDiscoveryMarkets,
  topPerformingMarketOnFocus,
  setTopPerformingMarketOnFocus,
  marketsToCompareList,
  setMarketsToCompareList,
  minROI,
  maxROI,
  isMarketDataAccessible,
  freeMarketDataKey,
  isTabChanging,
  filteredDiscoveryMarkets,
  selectedDiscoveryCountry,
  setSelectedDiscoveryCountry,
  allRawMarketData,
  mapWidth,
  setIsDynamicDataBtnVisible,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  zoom,
  setZoom,
  setBounds,
  visibleMapListings,
  setVisibleMapListings,
  mapViewportListings,
  setMapViewportListings,
  setSubmarketPolygonPaths,
  mapLoading,
  setMapLoading,
  multipleMarkersCallLoading,
  OnBoardingSearchBox,
  showViewingLisingFromPaginationAlert,
  setShowViewingLisingFromPaginationAlert,
  mapHeightOnMobileView,
  cachedMarketBedroomADR,
  cachedMarketData
}) => {
  const user = useSelector(state => (state as any).dashboard.user);
  const {
    polygon_to_use,
    isPolyToUseMultiPolygon,
    center_to_use,
    fixed_center_in_mobile_view,
    discovery_default_zoom_level,
    redirect_to_us,
  } = getDiscoveryMapViewParamsCountryWise(selectedDiscoveryCountry);

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: search.data.lat,
    lng: search.data.lng,
  });

  const [discoveryMarkers, setDiscoveryMarkers] = useState<(JSX.Element | undefined)[] | null>(
    null
  );

  const [mapCopy, setMapCopy] = useState<any>(null);
  const [mapsCopy, setMapsCopy] = useState<any>(null);
  const [searchPolygonCopy, setSearchPolygonCopy] = useState<any>(null);
  const [discoveryPolygonCopy, setDiscoveryPolygonCopy] = useState<any>(null);
  const [polygonPath, setPolygonPath] = useState<any>(null);

  const [polygonCopiesDiscovery, setPolygonCopiesDiscovery] = useState<any | null>(null);
  const [discoveryMapBounds, setDiscoveryMapBounds] = useState<GoogleMapReact.Bounds | null>(null);
  const [notSelectedDiscoveryCountryPolygons, setNotSelectedDiscoveryCountryPolygons] = useState<
    any[]
  >([]);
  const [notSelectedDiscoveryCountryPointer, setNotSelectedDiscoveryCountryPointer] = useState<
    JSX.Element[]
  >([]);

  const isMarketTab: boolean =
    dashboardLayoutView !== 'discovery' && dashboardLayoutView !== 'compare';

  const [polygonBounds, setPolygonBounds] = useState<{
    maxLat: number;
    minLat: number;
    maxLng: number;
    minLng: number;
  } | null>(null);

  const [isSearchModalOpen, setIsSearchModalOpen] = useState<
    'open' | 'alreadyOpened' | 'neverOpened'
  >('neverOpened');

  // toggles map center from different tabs
  useEffect(() => {
    if (!isMarketTab) {
      matches ? setMapCenter(fixed_center_in_mobile_view) : setMapCenter(center_to_use);
    } else {
      setTopPerformingMarketOnFocus(_ => null);
      setMapCenter({ lat: search.data.lat, lng: search.data.lng });
    }
  }, [dashboardLayoutView, selectedDiscoveryCountry]);

  // Explore markets of other country
  useEffect(() => {
    if (notSelectedDiscoveryCountryPolygons.length) {
      notSelectedDiscoveryCountryPolygons.forEach(polygon => {
        polygon.setMap(null);
      });
      setNotSelectedDiscoveryCountryPolygons([]);
    }
    setNotSelectedDiscoveryCountryPointer([]);

    if (selectedDiscoveryCountry == '') return;

    const tempNonSelectedMarketPolygonsArray: any[] = [];
    const tempNonSelectedMarketPointer: JSX.Element[] = [];

    ['US', 'GB', 'AU', 'CA'].forEach(countryCode => {
      if (selectedDiscoveryCountry !== countryCode) {
        const currentCountryGeometry = getDiscoveryMapViewParamsCountryWise(countryCode);
        const currentCountryPolygon = currentCountryGeometry.polygon_to_use;
        const curentCountryCenter = currentCountryGeometry.center_to_use;
        const isPolyToUseMultiPolygon = currentCountryGeometry.isPolyToUseMultiPolygon;

        if (
          mapCopy &&
          mapsCopy &&
          (dashboardLayoutView === 'discovery' || dashboardLayoutView === 'compare')
        ) {
          const paths = getPolygonPaths(
            isPolyToUseMultiPolygon ? '' : currentCountryPolygon,
            isPolyToUseMultiPolygon ? currentCountryPolygon : ''
          );
          const polygon = setPolygon(mapCopy, mapsCopy, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
          tempNonSelectedMarketPolygonsArray.push(polygon);
          tempNonSelectedMarketPointer.push(
            <DiscoverCountryPointer
              lat={curentCountryCenter.lat}
              lng={curentCountryCenter.lng}
              countryCode={countryCode as string}
              handleClick={() => {
                setSelectedDiscoveryCountry(countryCode as string);
                window.history.pushState('', '', '/markets/' + countryCode);
              }}
            />
          );
        }
      }
    });

    tempNonSelectedMarketPolygonsArray.length > 0 &&
      setNotSelectedDiscoveryCountryPolygons(tempNonSelectedMarketPolygonsArray);
    tempNonSelectedMarketPointer.length > 0 &&
      setNotSelectedDiscoveryCountryPointer(tempNonSelectedMarketPointer);
  }, [dashboardLayoutView, selectedDiscoveryCountry, mapCopy, mapsCopy]);

  const discoverMarketChip = (visibleDiscoveryMarkets: any = null) => {
    let clusteredDiscoveryMarkers: any[] = [];
    setDiscoveryMarkers(null);
    if (discoveryMarkets || visibleDiscoveryMarkets) {
      clusteredDiscoveryMarkers = (visibleDiscoveryMarkets
        ? visibleDiscoveryMarkets
        : discoveryMarkets
      )
        .filter(marketData => {
          return filteredDiscoveryMarkets.has(
            marketData.district + marketData.city + marketData.countryCode
          );
        })
        .slice(0, 10)
        .reverse()
        .map(marketData => {
          const [longitude, latitude]: [number, number] = marketData.center
            .slice(6, marketData.center.length - 1)
            .split(' ')
            .map(Number);

          return (
            <MarketPointer
              key={`listing-cluster-${marketData.district}-${
                marketData.ten_years_roi
              }-${Math.random()}`}
              id={marketData.district}
              lat={latitude}
              lng={longitude}
              countryCode={marketData.country_code}
              pointSize={20}
              name={marketData.district as string}
              score={marketData.ten_years_roi}
              data={marketData}
              isHighlighted={
                topPerformingMarketOnFocus &&
                topPerformingMarketOnFocus.district === marketData.district
              }
              marketsToCompareList={marketsToCompareList}
              setMarketsToCompareList={setMarketsToCompareList}
              dashboardLayoutView={dashboardLayoutView}
              minROI={minROI}
              maxROI={maxROI}
              isMarketDataAccessible={isMarketDataAccessible}
              currency={currency}
              freeMarketDataKey={freeMarketDataKey}
              handleOnSampleClick={handleOnSampleClick}
              marketLength={allRawMarketData ? allRawMarketData.length : 0}
            />
          );
        });

      setDiscoveryMarkers(clusteredDiscoveryMarkers);
    }
  };

  useEffect(() => {
    if (topPerformingMarketOnFocus) {
      let center = topPerformingMarketOnFocus.center;
      const { latitude, longitude } = extractLatLong(center);
      setMapCenter({ lat: latitude, lng: longitude });
    }
  }, [topPerformingMarketOnFocus]);

  useEffect(() => {
    discoveryMapBounds
      ? getVisibleDiscoveryPolygons(
          discoveryMapBounds as GoogleMapReact.Bounds,
          topPerformingMarketOnFocus,
          dummyMarketData,
          marketsToCompareList
        )
      : discoverMarketChip();
  }, [
    filteredDiscoveryMarkets,
    dashboardLayoutView,
    marketsToCompareList,
    selectedDiscoveryCountry,
    dummyMarketData,
  ]);

  const SearchBarModalComponent = (
    <SearchBarModal
      open={isSearchModalOpen}
      setOpen={setIsSearchModalOpen}
      OnBoardingSearchBox={() => OnBoardingSearchBox('map')}
    />
  );

  const getVisibleDiscoveryPolygons = (
    bounds: GoogleMapReact.Bounds,
    topPerformingMarketOnFocus: any = null,
    dummyMarketData: any[] = [],
    marketsToCompareList: any[] = []
  ) => {
    const visibleDiscoveryMarkets: any[] = [];
    if (topPerformingMarketOnFocus) {
      visibleDiscoveryMarkets.push(topPerformingMarketOnFocus);
    }

    if (marketsToCompareList) {
      for (let data of marketsToCompareList) {
        dummyMarketData &&
          dummyMarketData.forEach(market => {
            if (data[0] === market.district) {
              visibleDiscoveryMarkets.push(market);
            }
          });
      }
    }

    dummyMarketData &&
      dummyMarketData.forEach(market => {
        const isThisMarketCenterVisible = isPointWithinBounds(market.center, bounds);
        if (isThisMarketCenterVisible) {
          visibleDiscoveryMarkets.push(market);
        }
      });

    discoverMarketChip(visibleDiscoveryMarkets);
    setDiscoveryMarkets(mapWidth === 0 ? visibleDiscoveryMarkets : dummyMarketData);

    return visibleDiscoveryMarkets;
  };

  const alertIfUserOutOfRange = bounds => {
    try {
      if (isSearchModalOpen === 'alreadyOpened' || !polygonPath) return;
      // Convert map bounds to a Turf.js polygon
      const mapViewPolygon = turf.bboxPolygon([
        bounds.sw.lng,
        bounds.sw.lat, // southwest corner (lng, lat)
        bounds.ne.lng,
        bounds.ne.lat, // northeast corner (lng, lat)
      ]);
      // Convert your polygon array to a Turf.js polygon
      const turfPolygon = turf.polygon([polygonPath[0].map(point => [point.lng, point.lat])]);
      // Check if the polygon intersects with the map bounds
      const isPolygonInView = turf.intersect(turfPolygon, mapViewPolygon) !== null;

      if (!isPolygonInView) {
        setIsSearchModalOpen('open');
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateMarketDetailsPremiumDataVisibility = (bounds: Bounds, isUserAllowed: boolean) => {
    const { isInside: isPolygonInsideOfBounds } = comparePolygonLimitsToBoundary(
      polygonBounds,
      bounds,
      20
    );

    if (!isUserAllowed && !isPolygonInsideOfBounds) {
      setShowPremiumDataDynamic(false);
    } else if (isPolygonInsideOfBounds) {
      setShowPremiumDataDynamic(true);
    }
  };

  const handleMapChange = async ({ center, bounds, zoom }: GoogleMapReact.ChangeEventValue) => {
    if (isTabChanging) {
      return;
    }

    if (isMarketTab) {
      alertIfUserOutOfRange(bounds);
      setMapLoading(true);
      showViewingLisingFromPaginationAlert === 'showing' &&
        setShowViewingLisingFromPaginationAlert('shown');

      visibleMapListings && visibleMapListings.length > 0 && setIsDynamicDataBtnVisible(true);
      const boundsObj = formatGoogleMapsBounds(bounds);
      setBounds(boundsObj);
      setZoom(zoom);

      const polygonArray: [number, number][] = [
        [boundsObj.northEast.lat, boundsObj.northEast.lng], // Top-right
        [boundsObj.southWest.lat, boundsObj.northEast.lng], // Bottom-right
        [boundsObj.southWest.lat, boundsObj.southWest.lng], // Bottom-left
        [boundsObj.northEast.lat, boundsObj.southWest.lng], // Top-left
        [boundsObj.northEast.lat, boundsObj.northEast.lng], // Close the polygon (back to top-right)
      ];
      setSubmarketPolygonPaths(polygonArray);

      if (!listings.loading && !otaListings.loading) {
        const visibleListings = sortListingBasedOnKey(
          getVisibleListings(
            listings,
            boundsObj,
            filterActive,
            filteredListings as string[]
          ) as Listing[],
          'performance'
        );

        if (visibleListings.length >= 22) {
          setVisibleMapListings(visibleListings.slice(0, 22));
        } else {
          const asyncVisibleListings = await getDynamicMarkers(boundsObj, search);
          setVisibleMapListings(asyncVisibleListings);
        }
        setMapViewportListings({ count: visibleListings.length, loading: false });

        const boundedOTAListingIDs = getBoundedOTAListings(otaListings.data, boundsObj);
        const visibleOTAListingIDs = new Set(
          getVisibleOTAListings(
            boundedOTAListingIDs,
            filteredOTAListings ? filteredOTAListings : boundedOTAListingIDs
          )
        );
      } else {
        const asyncVisibleListings = await getDynamicMarkers(boundsObj, search);
        setVisibleMapListings(asyncVisibleListings);
      }

      setListingsGridStartIndexOffset(_ => 0);
      polygonBounds && updateMarketDetailsPremiumDataVisibility(boundsObj, isUserAllowed);

      setTimeout(() => {
        setMapLoading(false);
      }, 500);
    } else {
      setDiscoveryMapBounds(bounds);

      getVisibleDiscoveryPolygons(
        bounds,
        topPerformingMarketOnFocus,
        dummyMarketData,
        marketsToCompareList
      );
    }
  };

  const setPolygon = (
    map,
    maps,
    paths: Coordinates[][],
    strokeColor: string,
    fillColor: string,
    strokeOpacity = 0.8,
    strokeWeight = 2,
    fillOpacity = 0.12
  ) => {
    const polygon = new maps.Polygon({
      paths: paths,
      strokeColor: strokeColor,
      strokeOpacity: strokeOpacity,
      strokeWeight: strokeWeight,
      fillColor: fillColor,
      fillOpacity: fillOpacity,
    });
    polygon.setMap(map);

    // set dashboard to map view on interaction with map/polygons
    maps.event.addListener(polygon, 'click', function(e) {
      if (!localStorage.getItem('map-flag')) {
        setDashboardLayoutView('map');
        localStorage.setItem('map-flag', 'true');
      }
    });

    maps.event.addListener(map, 'dragend', function(e) {
      if (!localStorage.getItem('map-flag')) {
        setDashboardLayoutView('map');
        localStorage.setItem('map-flag', 'true');
      }
    });

    return polygon;
  };

  const apiIsLoaded = (map, maps, polygonRaw: string, multiPolygonRaw: string) => {
    if (isMarketTab) {
      polygonCopiesDiscovery &&
        Object.keys(polygonCopiesDiscovery).forEach(polygonID => {
          polygonCopiesDiscovery[polygonID].setMap(null);
        });

      const paths = getPolygonPaths(polygonRaw, multiPolygonRaw);
      setPolygonPath(paths);
      const polygon = setPolygon(map, maps, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
      applySmartBounds(map, maps, {} as Bounds, [polygon]);
      setSearchPolygonCopy(polygon);
      setPolygonBounds(calculateBounds(paths));
    }

    setMapCopy(map);
    setMapsCopy(maps);
  };

  useEffect(() => {
    if (isMarketTab && mapCopy && mapsCopy && search.data) {
      discoveryPolygonCopy && discoveryPolygonCopy.setMap(null);
      searchPolygonCopy && searchPolygonCopy.setMap(null);
      polygonCopiesDiscovery &&
        Object.keys(polygonCopiesDiscovery).forEach(polygonID => {
          polygonCopiesDiscovery[polygonID].setMap(null);
        });
      setPolygonCopiesDiscovery(null);

      setMapCenter({ lat: search.data.lat, lng: search.data.lng });

      const paths = getPolygonPaths(search.data.polygon, search.data.multiPolygon);
      const polygon = setPolygon(mapCopy, mapsCopy, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
      applySmartBounds(mapCopy, mapsCopy, {} as Bounds, [polygon]);
      setSearchPolygonCopy(polygon);
      setPolygonBounds(calculateBounds(paths));
    } else {
      searchPolygonCopy && searchPolygonCopy.setMap(null);
      !isMarketTab && setZoom(discovery_default_zoom_level);
    }
  }, [
    dashboardLayoutView,
    marketsToCompareList,
    selectedDiscoveryCountry,
    filteredDiscoveryMarkets,
  ]);

  useEffect(() => {
    if (
      mapCopy &&
      mapsCopy &&
      (dashboardLayoutView === 'discovery' || dashboardLayoutView === 'compare')
    ) {
      discoveryPolygonCopy && discoveryPolygonCopy.setMap(null);
      const paths = getPolygonPaths(
        isPolyToUseMultiPolygon ? '' : polygon_to_use,
        isPolyToUseMultiPolygon ? polygon_to_use : ''
      );
      const polygon = setPolygon(mapCopy, mapsCopy, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
      setDiscoveryPolygonCopy(polygon);
    } else {
      discoveryPolygonCopy && discoveryPolygonCopy.setMap(null);
    }
  }, [mapCopy, mapsCopy, dashboardLayoutView, selectedDiscoveryCountry]);

  const markers = useMemo(() => {
    if (visibleMapListings && visibleMapListings.length) {
      let revenueMarkerCount = 0;
      return visibleMapListings.map((listing: Listing, index: number) => {
        const item = listing;
        if (index % 2 == 0) {
          revenueMarkerCount = revenueMarkerCount + 1;
        }
        const bedroom = listing.bedrooms.toLowerCase() == 'studio' ? 0 : listing.bedrooms;
        const referenceADR = cachedMarketBedroomADR ? cachedMarketBedroomADR[bedroom] : null;

        return (
          <Pointer
            key={index + '-map-pointer'}
            lat={item.latitude}
            lng={item.longitude}
            setSubscriptionModalOpen={setSubscriptionModalOpen}
            data={item}
            canDelete={true}
            isDisabled={isDisabled}
            currency={currency}
            subscriptionModalOpen={subscriptionModalOpen}
            multipleBookingMarkersCallLoading={multipleBookingMarkersCallLoading}
            market={search.data.name}
            handleOnSampleClick={reqPlan => handleOnSampleClick(reqPlan, 'individualListingsKPI')}
            user={user}
            topPerformingListingOnFocus={topPerformingListingOnFocus}
            isEstimator={false}
            openDemoBookingForm={openDemoBookingForm}
            setDemoFormHeadingFor={setDemoFormHeadingFor}
            showRevenuePotential={index % 2 == 0 && revenueMarkerCount <= 10 ? true : false}
            referenceADR={referenceADR}
            cachedMarketData={cachedMarketData}
          />
        );
      });
    }
    return <></>;
  }, [visibleMapListings, currency, topPerformingListingOnFocus]);

  // const otaMarkers = useMemo(() => {
  //   return;
  //   if (visibleOTAListingIDs.length) {
  //     const safeVisibleOTAListings = safeOTAMarkersLoad(visibleOTAListings.data, isUserAllowed);
  //     return safeVisibleOTAListings.map((data, index) => (
  //       <SimplePointer lat={data.lat} lng={data.lng} data={data} />
  //     ));
  //   }
  //   return null;
  // }, [visibleOTAListingIDs]);

  return (
    <>
      {!mapViewportListings.loading && isMarketTab && (
        <MapListingCountInfoBox
          multipleMarkersCallLoading={multipleMarkersCallLoading}
          listingsLength={mapViewportListings.count}
          otaListingsLength={0}
          isMapChanging={false}
          open={!mapViewportListings.loading && isMarketTab}
        />
      )}

      <GoogleMapReact
        bootstrapURLKeys={{
          key: GOOGLE_MAPS_API_KEY,
          libraries: ['drawing', 'places', 'visualization'],
          id: 'dashboard2',
        }}
        center={mapCenter}
        options={map => mapOptions(map, false)}
        zoom={zoom}
        resetBoundsOnResize={true}
        onChange={handleMapChange}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) =>
          apiIsLoaded(map, maps, search.data.polygon, search.data.multiPolygon)
        }
        {...(matches && { style: { height: `${mapHeightOnMobileView}vh` } })}
      >
        {/* {!isTabChanging &&
          isMarketTab &&
          !visibleOTAListings.loading &&
          visibleListingIDs.length <= 60 &&
          otaMarkers} */}
        {!isTabChanging && isMarketTab && visibleMapListings ? markers : <></>}
        {!isTabChanging &&
          (dashboardLayoutView === 'discovery' || dashboardLayoutView === 'compare') &&
          discoveryMarkers}
        {notSelectedDiscoveryCountryPointer.length > 0 && notSelectedDiscoveryCountryPointer}
      </GoogleMapReact>

      {mapLoading && (
        <div className="loadingContainer">
          <div className="dotsContainer">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      )}

      <MapFilter
        toggleDrawer={toggleDrawer}
        setFilterVal={setFilterVal}
        applyFilter={applyFilter}
        filterVal={filterVal}
        filterActive={filterActive}
        setFilterActive={setFilterActive}
        isDisabled={isDisabled}
        handleOnSampleClick={handleOnSampleClick}
        listingsCount={listings.data ? Object.keys(listings.data).length : 0}
        setZoom={setZoom}
        onlyShowZoom={dashboardLayoutView !== 'map' || matches}
        onZoomInClick={() => {
          if (
            dashboardLayoutView !== 'map' &&
            dashboardLayoutView !== 'discovery' &&
            dashboardLayoutView !== 'compare'
          ) {
            setDashboardLayoutView('map');
            logAmplitudeEvent('map_viewed_in_overview', { cta: 'zoom_btn' });
          }
        }}
        dashboardLayoutView={dashboardLayoutView}
        noDataInMarket={noDataInMarket}
      />

      {SearchBarModalComponent}
    </>
  );
};

export default React.memo(Map);
